/* eslint-disable @typescript-eslint/no-unsafe-assignment */
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { delay } from 'betterplace-utils';
import _, { get, merge } from 'lodash';
import { useTranslations, useLocale } from 'next-intl';
import { type BaseSyntheticEvent, type SubmitEvent, useActionState, useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { type FieldPath, type FieldValues, type Path, type Resolver, useForm } from 'react-hook-form';
import type { z } from 'zod';
import type { ImperativeSubmit, SetterTools, UseActionFormProps, UseActionFormReturn } from './types';
import useHydrateFormWithUserData from './useHydrateFormWithUserData';
import useTriggerOnFormValueChanges from './useTriggerOnFormValueChanges';
import type { BaseFormOperation, BaseFormState } from '@/form/types';
import { errorToFieldErrors, fromFormData, toFormData } from '@/form/utils';
import { AppError, fromErrorObject, toErrorObject } from '@/helpers/errors';
import { createSyntheticEvent } from '@/helpers/utils';
import useI18nZodErrorMap from '@/i18n/zod/useI18nZodErrorMap';

/**
 * This hook combines a form action (that you can create by calling toFormAction on a normal server action)
 * to provide a server-side validation, useFormState from React, for cross-call server-side state persistence
 * and React Hook Form for client-side validation and client, it's return is mostly compatible with useForm from RHF
 * @param options useActionForm configuration. See UseActionFormProps for more details
 * @returns
 */
function useActionForm<
// eslint-disable-next-line @typescript-eslint/no-explicit-any
TSchema extends z.ZodType<any, any>, TOperation extends BaseFormOperation, TState extends BaseFormState<TOperation>>({
  action,
  actionContext,
  schema,
  defaultValues: defaultValues_,
  getErrorMap,
  beforeFormSubmit,
  afterClientSubmitError,
  afterClientSubmitSuccess,
  afterSubmitSuccess,
  afterSubmitError,
  defaultErrorMessage,
  id: externalId,
  validateAll,
  ...props
}: UseActionFormProps<TSchema, TOperation, TState>): UseActionFormReturn<z.infer<TSchema>, TState['data']> {
  const locale = useLocale();
  const [serverError_, setServerError_] = useState<AppError | null>(null);
  const t = useTranslations('nextjs');
  useI18nZodErrorMap();
  const [serverState, clientAction, pending] = useActionState(action, {
    ...actionContext,
    locale,
    fields: toFormData(defaultValues_)
  } as Awaited<TState>);
  const defaultValues = useMemo(() => ({
    ...defaultValues_,
    ...fromFormData<z.infer<TSchema>>(serverState.fields)
  }), [serverState.fields, defaultValues_]);
  const [submitActionInProgress, startSubmitAction] = useTransition();
  const [busy, setBusy] = useState(false);
  const resolver = useMemo(() => zodResolver(schema, {
    error: getErrorMap?.(t, locale)
  }), [schema, getErrorMap, t, locale]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as Resolver<z.output<TSchema>, any, z.output<TSchema>>;
  const {
    handleSubmit,
    formState: clientFormState,
    getValues,
    reset: reset_,
    setError,
    setFocus,
    setValue,
    clearErrors,
    resetField,
    trigger,
    ...rest
  } = useForm({
    resolver,
    defaultValues,
    // errors: external,
    ...props
  });
  const {
    id
  } = useHydrateFormWithUserData(setValue, defaultValues_, externalId);
  const tools: SetterTools<z.infer<TSchema>> = useMemo(() => ({
    setBusy,
    setError,
    setFocus,
    setValue,
    clearErrors,
    resetField
  }), [clearErrors, setError, setFocus, setValue, resetField]);
  const handleServerError = useCallback(async (serverError: AppError) => {
    const input = getValues();
    for (const e of serverError.errors) {
      if (!('path' in e)) continue;
      const fieldPath = e.path.join('.') as FieldPath<z.infer<TSchema>>;
      tools.setValue(fieldPath, get(input, fieldPath), {
        shouldTouch: true,
        shouldValidate: true
      });
      tools.resetField(fieldPath, {
        keepTouched: true,
        keepDirty: false,
        keepError: true,
        defaultValue: getValues(fieldPath)
      });
    }
    setServerError_(serverError);
    if (typeof afterSubmitError !== 'function') return;
    // ensure the afterSubmitError is called on the next tick
    await delay(0);
    const errors = toErrorObject<z.infer<TSchema>>(serverError.errors);
    await afterSubmitError(errors, input, tools, {
      shouldSetErrors: true
    });
  }, [getValues, afterSubmitError, tools]);
  const onSubmitInner = useCallback(async (input_0: FieldValues, event?: BaseSyntheticEvent) => {
    if (!event) {
      throw new TypeError('Event is missing');
    }
    await afterClientSubmitSuccess?.(input_0 as z.output<TSchema>, tools);
    const formData = toFormData(input_0);
    const result = (await action(serverState, formData)) ?? {
      error: new AppError([{
        message: t('errors.messages.try_again'),
        type: 'GeneralError'
      }]),
      data: undefined,
      locale
    };
    const {
      data,
      error
    } = result;
    if (error) {
      await handleServerError(result.error);
    } else {
      setServerError_(null);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      await afterSubmitSuccess?.(result.data, input_0 as z.output<TSchema>, tools);
    }
    return {
      data,
      error
    };
  }, [afterClientSubmitSuccess, tools, action, serverState, t, locale, afterSubmitSuccess, handleServerError]);
  const serverErrors = useMemo(() => {
    return errorToFieldErrors(serverError_ ?? serverState.error, defaultErrorMessage);
  }, [serverError_, serverState.error, defaultErrorMessage]);

  /**
   * Fields to be merged with client-side errors, we assume that dirty fields do
   * were already processed by the user, so the error can go away
   */
  const activeServerErrors = useMemo(() => {
    const {
      dirtyFields
    } = clientFormState;
    const errors_0 = (serverError_ ?? serverState.error)?.errors.filter(error_0 => {
      const path = 'path' in error_0 ? error_0.path : [];
      if (!path.length) return true;
      const pathStr = path.join('.');
      const dirty = get(dirtyFields, pathStr);
      return !dirty;
    }) ?? [];
    return errorToFieldErrors({
      errors: errors_0
    }, defaultErrorMessage);
  }, [serverError_, serverState.error, defaultErrorMessage, clientFormState]);
  const serverStateIsValid = useMemo(() => {
    return !serverState.error?.errors.length;
  }, [serverState.error]);
  const formState = useMemo(() => {
    const {
      errors: errors_1,
      isValid,
      isDirty,
      isLoading,
      isSubmitSuccessful,
      isSubmitted,
      isSubmitting,
      isValidating,
      submitCount,
      dirtyFields: dirtyFields_0,
      touchedFields,
      disabled,
      validatingFields,
      isReady
    } = clientFormState; // clientFormState is a Proxy, so we cannot simply spread values here

    return {
      submitCount,
      dirtyFields: dirtyFields_0,
      touchedFields,
      isDirty,
      isLoading,
      isSubmitSuccessful,
      isSubmitted,
      isSubmitting: isSubmitting || busy || submitActionInProgress || pending,
      isValidating,
      isValid: isValid && serverStateIsValid,
      disabled,
      validatingFields,
      isReady,
      errors: merge(activeServerErrors, errors_1)
    };
  }, [clientFormState, serverStateIsValid, activeServerErrors, busy, submitActionInProgress, pending]);
  const onSubmit = useCallback((event_0: SubmitEvent<HTMLFormElement>) => {
    event_0.preventDefault();
    startSubmitAction(async () => {
      const values = getValues();
      await beforeFormSubmit?.(values, tools);
      await handleSubmit(onSubmitInner, async errors_2 => {
        const array = fromErrorObject(errors_2);
        for (const err of array) {
          if (err.type === 'FieldError') {
            const field = err.path.join('.') as Path<z.infer<TSchema>>;
            setValue(field, values[field], {
              shouldTouch: true
            });
            continue;
          }
        }
        await afterClientSubmitError?.(errors_2, values, tools);
      })(event_0);
    });
  }, [getValues, beforeFormSubmit, tools, handleSubmit, onSubmitInner, afterClientSubmitError, setValue]);
  const submit: ImperativeSubmit<z.infer<TSchema>, TState['data']> = useCallback(() => {
    return new Promise((resolve, reject) => {
      startSubmitAction(async () => {
        try {
          await beforeFormSubmit?.(getValues(), tools);
          await handleSubmit(async input_1 => {
            const formEvent = createSyntheticEvent(new FormDataEvent('submit', {
              cancelable: true,
              formData: new FormData()
            }));
            const res = await onSubmitInner(input_1, formEvent);
            if (res.data) {
              return resolve({
                data: res.data,
                input: input_1
              });
            }

            // if we have only field errors these are simply validation errors
            if (!res.error?.errors.find(error_2 => error_2.type !== 'FieldError')) {
              return reject(new Error(ActionFormSubmitErrorTypes.ValidationError));
            }
            reject(new Error(`${ActionFormSubmitErrorTypes.ServerError} - errors: ${JSON.stringify(res.error?.errors)}`));
          }, async errors_3 => {
            const values_0 = getValues();
            await afterClientSubmitError?.(errors_3, values_0, tools);
            reject(new Error(ActionFormSubmitErrorTypes.ValidationError));
          })();
        } catch (error_1) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          reject(error_1 as Error);
        }
      });
    });
  }, [beforeFormSubmit, getValues, tools, handleSubmit, onSubmitInner, afterClientSubmitError]);

  // immediately trigger validation for the default values
  useEffect(() => {
    void trigger();
  }, [trigger]);
  const control = rest.control;
  useTriggerOnFormValueChanges({
    trigger,
    control,
    validateAll
  });
  const reset: typeof reset_ = useCallback(v => v ? reset_(v) : reset_(defaultValues_), [reset_, defaultValues_]);
  return {
    ...rest,
    reset,
    setBusy,
    setFocus,
    setValue,
    resetField,
    setError,
    clearErrors,
    getValues,
    submit,
    formState,
    handleSubmit,
    onSubmit,
    defaultValues,
    trigger,
    action: clientAction,
    id,
    serverErrors
  };
}
export const ActionFormSubmitErrorTypes = {
  ValidationError: 'ACTION_FORM_VALIDATION_ERROR',
  ServerError: 'ACTION_FORM_SERVER_ERROR'
} as const;
export default useActionForm;